import { BaseCanvasPack } from "./BaseCanvasPack";

export default class ZenGardenCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // Light sandy beige background
    context.fillStyle = "#f5f5dc"; // Beige
    context.fillRect(0, 0, width, height);

    // Raked sand texture (concentric circles or waves)
    context.save();
    context.strokeStyle = "#e8e8c8";
    context.lineWidth = 2;
    
    // Draw some raked lines
    for (let i = 0; i < height; i += 15) {
        context.beginPath();
        // Wavy lines
        for (let x = 0; x <= width; x += 10) {
            const y = i + Math.sin(x * 0.05) * 3;
            if (x === 0) context.moveTo(x, y);
            else context.lineTo(x, y);
        }
        context.stroke();
    }
    context.restore();

    if (frameTime !== undefined) {
      this.drawRipples(context, width, height, frameTime);
      this.drawCherryBlossoms(context, width, height, frameTime);
    }
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    const borderWidth = 8;
    
    // Bamboo color
    const bambooColor = "#a1887f"; // Light brown/bamboo
    const bambooDark = "#5d4037"; // Darker for joints
    
    context.lineWidth = borderWidth;
    context.lineCap = "round";
    
    // Helper to draw a bamboo segment
    const drawBambooLine = (x1: number, y1: number, x2: number, y2: number, vertical: boolean) => {
        context.strokeStyle = bambooColor;
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
        
        // Draw joints
        context.fillStyle = bambooDark;
        const length = vertical ? Math.abs(y2 - y1) : Math.abs(x2 - x1);
        const segmentLength = 40;
        const segments = Math.floor(length / segmentLength);
        
        for (let i = 1; i < segments; i++) {
            const pos = i * segmentLength;
            if (vertical) {
                context.fillRect(x1 - borderWidth/2 - 1, y1 + pos, borderWidth + 2, 2);
            } else {
                context.fillRect(x1 + pos, y1 - borderWidth/2 - 1, 2, borderWidth + 2);
            }
        }
    };

    // Draw 4 sides
    drawBambooLine(borderWidth/2, borderWidth/2, width - borderWidth/2, borderWidth/2, false); // Top
    drawBambooLine(borderWidth/2, height - borderWidth/2, width - borderWidth/2, height - borderWidth/2, false); // Bottom
    drawBambooLine(borderWidth/2, borderWidth/2, borderWidth/2, height - borderWidth/2, true); // Left
    drawBambooLine(width - borderWidth/2, borderWidth/2, width - borderWidth/2, height - borderWidth/2, true); // Right

    // Tied corners (rope effect)
    context.strokeStyle = "#3e2723"; // Dark rope
    context.lineWidth = 2;
    const cornerSize = borderWidth + 4;
    
    const drawTie = (x: number, y: number) => {
        // Cross pattern
        context.beginPath();
        context.moveTo(x - cornerSize/2, y - cornerSize/2);
        context.lineTo(x + cornerSize/2, y + cornerSize/2);
        context.moveTo(x + cornerSize/2, y - cornerSize/2);
        context.lineTo(x - cornerSize/2, y + cornerSize/2);
        context.stroke();
    };

    drawTie(borderWidth/2, borderWidth/2);
    drawTie(width - borderWidth/2, borderWidth/2);
    drawTie(borderWidth/2, height - borderWidth/2);
    drawTie(width - borderWidth/2, height - borderWidth/2);
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // Maybe some falling petals in front?
  }

  private drawRipples(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.strokeStyle = "rgba(100, 100, 100, 0.1)";
    context.lineWidth = 2;

    // Slow moving ripples
    const rippleCount = 3;
    for (let i = 0; i < rippleCount; i++) {
        const seed = i * 5000;
        const progress = ((frameTime + seed) % 8000) / 8000;
        const radius = progress * Math.max(width, height) * 0.8;
        const alpha = 1 - progress;
        
        context.globalAlpha = alpha;
        context.beginPath();
        context.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
        context.stroke();
    }
    context.restore();
  }

  private drawCherryBlossoms(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.fillStyle = "#ffcdd2"; // Pink

    const petalCount = 15;
    for (let i = 0; i < petalCount; i++) {
        const seed = i * 123.45;
        // Falling down and swaying
        const y = ((frameTime * 0.05 + seed * 100) % (height + 20)) - 10;
        const sway = Math.sin(frameTime * 0.002 + seed) * 30;
        const x = ((seed * 100) % width) + sway;
        
        const size = 3 + Math.sin(seed) * 1;
        const rotation = frameTime * 0.001 + seed;

        context.save();
        context.translate(x, y);
        context.rotate(rotation);
        
        // Draw petal (simple oval)
        context.beginPath();
        context.ellipse(0, 0, size, size * 0.6, 0, 0, Math.PI * 2);
        context.fill();
        
        context.restore();
    }
    context.restore();
  }

  getBorderStyle(): string {
    return "8px solid #8d6e63";
  }

  getBoxShadow(frameTime?: number): string {
    return "0 4px 10px rgba(0, 0, 0, 0.1)";
  }

  getBackdropFilter(): string {
    return "none";
  }
}
